import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import {
  formInit,
  renderFields,
  renderCredentialsFields,
  renderMock,
} from "./config";
import Button from "../../../atoms/Buttons/Button";
import DirectMailActionButton from "../../../organisms/Buttons/PreApprovedMailAction";
import CaptchaWidget from "../../../organisms/Captcha/CaptchaWidget";
import arrow from "../../../../assets/home/arrow-right-white.png";
import { parseFormToFormat } from "../../../../utils/form/parsers";
import {
  sendLeadRemarketing,
  sendLandingPageRequest,
} from "../../../../api/application";
import { validateForm } from "./validate";
import useCaptcha from "../../../../hooks/useCaptcha";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  row-gap: 2.4rem;

  & .terms-wrapper {
    font-size: 12px;
  }

  .fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .personal-info-fields {
    grid-gap: 1.2rem;
    row-gap: 2.4rem;
  }

  .button-wrapper {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    gap: 1.8rem;

    button[type="submit"] {
      min-width: 190px;
    }

    /* Takes the place of the arrow while the lead is submitting. The loaders
     * under molecules/Loaders are full-container overlays with hard-coded
     * colours, which would cover the button and clash on the landers, so this
     * one is inline and drawn in currentColor -- white on the home button, and
     * whatever each lander sets on its own. 16px matches the arrow, so the
     * label does not shift when they swap. */
    .btn-spinner {
      box-sizing: border-box;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      border: 2px solid currentColor;
      border-right-color: transparent;
      border-radius: 50%;
      animation: apply-submit-spin 0.7s linear infinite;
    }

    @keyframes apply-submit-spin {
      to {
        transform: rotate(360deg);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .btn-spinner {
        animation-duration: 2.4s;
      }
    }

    @media screen and (max-width: 436px) {
      flex-direction: column;
    }
  }

  @media screen and (max-width: 991px) {
    & form {
      min-height: 385px;
    }
  }
`;

/**
 * `placeholders` optionally overrides individual field placeholders by name,
 * e.g. { reason: "What's it for?" }. Callers that omit it keep the defaults from
 * ./config, so /landing, /landing-2 and the home page are unaffected.
 */
const FormComponent = ({
  applyButtonLabel = "Get Started",
  // defaulted so TS treats the prop as optional for the existing JS/TS callers
  placeholders = {},
}) => {
  const withPlaceholder = (field) =>
    placeholders[field.name]
      ? { ...field, placeholder: placeholders[field.name] }
      : field;

  const [form, setForm] = useState(formInit());
  const [isLoading, setLoading] = useState(false);
  const { captchaConfig, captchaToken, captchaProvider, onVerify, onExpire } =
    useCaptcha();
  const { search } = useLocation();
  const history = useHistory();

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prevState) => ({
      ...prevState,
      [name]: { ...prevState[name], value, message: "" },
    }));
  };

  useEffect(() => {
    window.analytics?.page("Application Form");
  });

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // don't save partial ssn
    const [isValid, updatedForm] = validateForm(form);
    if (!isValid) {
      setForm(updatedForm);
      return;
    }

    if (captchaConfig?.enabled && !captchaToken) {
      return;
    }

    const payload = parseFormToFormat(form);

    const utm = new URLSearchParams();
    const params = new URLSearchParams(search);
    for (let [value, key] of params) {
      if (key.indexOf("utm_")) {
        utm.set(value, key);
      }
    }

    setLoading(true);
    console.log("::::::::CallingRemarketingLandingForm");
    // const response = await sendLeadRemarketingToPayplan({
    //   tenantKey: process.env.REACT_APP_PAYPLAN_TENANT_KEY,
    //   merchantKey: process.env.REACT_APP_PAYPLAN_MERCHANT_KEY,
    //   loanAmount: parseInt(payload.requestedLoan),
    //   reason: payload.reason,
    //   email: payload.email,
    //   ssn: payload.ssn,
    //   cellPhone: payload.phoneNumber,
    // });
    if (!payload.mockData || payload.mockData === "") {
      payload.mockData = "{}";
    }
    const response = await sendLandingPageRequest({
      email: payload.email,
      ssnNumber: payload.ssn,
      phone: payload.phoneNumber,
      reason: payload.reason,
      utm: utm.toString(),
      requestedLoanAmount: payload.requestedLoan,
      originationUrl: window.location.href,
      mockData: JSON.parse(payload.mockData),
      captchaToken: captchaToken || undefined,
      captchaProvider: captchaProvider || undefined,
    });
    setLoading(false);

    window.analytics?.identify({
      email: payload.email,
      utm: utm.toString(),
      originationUrl: window.location.href,
    });

    //localStorage.clear();
    const { leadType, link, payplanExist } = response?.data || {};
    /* `link` is required on both redirect paths. The leadType branch assigned it
     * to window.location.href unchecked, so a response carrying a leadType but
     * no link navigated to the literal string "undefined". Requiring it here
     * sends those to the thankyou page instead of a dead navigation. */
    if (link && (payplanExist || (!response.error && leadType))) {
      window.location.href = link;
    } else {
      // localStorage.setItem("isProcessingApplication", true);
      history.push("/application/thankyou", {
        /* optional on `error` too: it is null whenever the request succeeded but
         * had no link to follow, and reading `.message` off it threw there,
         * which lost the navigation entirely. */
        message: response?.error?.message,
      });
    }
  };

  return (
    <Form onSubmit={onSubmitHandler}>
      <div className="fields">
        {renderFields(form).map(({ component: Component, ...field }) => {
          const f = withPlaceholder(field);
          return <Component key={f.name} {...f} onChange={onChange} />;
        })}
      </div>
      <div className="fields personal-info-fields">
        {renderCredentialsFields(form).map(
          ({ component: Component, ...field }) => {
            const f = withPlaceholder(field);
            return <Component key={f.name} {...f} onChange={onChange} />;
          },
        )}
      </div>
      {/* check if has showMock in localstorage then render mock field */}
      {localStorage.getItem("showMock") && (
        <div className="fields">
          {renderMock(form).map(({ component: Component, ...field }) => {
            return (
              <Component key={field.name} {...field} onChange={onChange} />
            );
          })}
        </div>
      )}
      {captchaConfig?.enabled && captchaConfig.siteKey && (
        <CaptchaWidget
          provider={captchaConfig.provider}
          siteKey={captchaConfig.siteKey}
          fallbackSiteKey={captchaConfig.fallbackSiteKey}
          onVerify={onVerify}
          onExpire={onExpire}
        />
      )}
      <div className="button-wrapper">
        <Button
          disabled={isLoading || (captchaConfig?.enabled && !captchaToken)}
          type="submit"
          variant="primary"
          className="button-contained"
          aria-busy={isLoading}
        >
          {applyButtonLabel}
          {isLoading ? (
            <span className="btn-spinner" aria-hidden="true" />
          ) : (
            <img src={arrow} alt="arrow-right" />
          )}
        </Button>

        <DirectMailActionButton />
      </div>
    </Form>
  );
};

export default FormComponent;
